// src/components/TeacherHPCReportPage.jsx - Copy of HPCReportPage for teachers
import AssessmentIconSVG from './svg/AssessmentIconSVG';
import PartA1 from "./report/PartA1";
import PartA2 from "./report/PartA2";
import PartBDomainPage from "./report/PartBDomainPage";
import TeacherFeedbackPage from "./report/TeacherFeedbackPage";
import PartC from "./report/PartC";
import React, { useEffect, useRef, useState } from 'react';
import { exportToPdf } from "./exportToPdf";
import { API_BASE_URL } from "../../../config";

// Helper function to safely format a date string for display
const displayDate = (dateString) => {
    if (!dateString) return "N/A";
    try {
        const date = new Date(dateString);
        if (isNaN(date.getTime())) {
            console.warn("Invalid date string provided for display:", dateString);
            return "Invalid Date";
        }
        return date.toLocaleDateString('en-GB', {
            year: 'numeric',
            month: '2-digit',
            day: '2-digit'
        });
    } catch (e) {
        console.error("Error formatting date for display:", dateString, e);
        return "Error Date";
    }
};

const TeacherHPCReportPage = ({ studentEmail, studentName, onClose, visible }) => {
    const [reportData, setReportData] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [exportingPdf, setExportingPdf] = useState(false);
    const reportRef = useRef(null);

    useEffect(() => {
        if (visible && studentEmail) {
            fetchReportData();
        }
    }, [visible, studentEmail]);

    const fetchReportData = async () => {
        setLoading(true);
        setError(null);
        setReportData(null);
        try {
            const accessToken = localStorage.getItem("accessToken");
            if (!accessToken) {
                console.error("Access token is missing or null!");
                setError("Authentication required. Please log in again.");
                setLoading(false);
                return;
            }

            // Using the new teacher endpoint with student email
            const response = await fetch(`${API_BASE_URL}/teacher/students/${studentEmail}/hpc-report`, {
                headers: {
                    'Authorization': `Bearer ${accessToken}`,
                    'Content-Type': 'application/json'
                }
            });

            if (!response.ok) {
                const errorBody = await response.text();
                throw new Error(`HTTP error ${response.status}: ${response.statusText}. Details: ${errorBody}`);
            }

            const data = await response.json();

            if (Object.keys(data).length === 0) {
                console.warn("Fetched data is empty. Report will reflect this.");
                setReportData({});
            } else {
                // Pre-process dates and ensure data integrity
                const processedData = { ...data };

                if (processedData.part_a_1) {
                    if (processedData.part_a_1.date_of_birth) {
                        processedData.part_a_1.date_of_birth = displayDate(processedData.part_a_1.date_of_birth);
                    }
                    if (!processedData.part_a_1.student_name) {
                        processedData.part_a_1.student_name = studentName || "Student";
                    }
                }

                if (processedData.part_a_2 && processedData.part_a_2.birthday) {
                    processedData.part_a_2.birthday = processedData.part_a_2.birthday;
                }

                setReportData(processedData);
            }
        } catch (e) {
            console.error("Failed to fetch report data:", e);
            setError(e.message || "An unknown error occurred while fetching the report.");
            setReportData(null);
        } finally {
            setLoading(false);
        }
    };

    const handleExportPdf = async () => {
        if (reportRef.current) {
            setExportingPdf(true);
            try {
                await exportToPdf(
                    'teacher-hpc-report-content',
                    `HPC_Report_${studentName?.replace(/\s/g, '_') || 'Student'}.pdf`
                );
            } catch (error) {
                console.error("Error exporting PDF:", error);
                alert("There was an error generating the PDF. Please try again.");
            } finally {
                setExportingPdf(false);
            }
        } else {
            alert("Report content is not ready for export.");
        }
    };

    if (!visible) return null;

    // Define page numbers based on fixed structure
    let pageCounter = { current: 2 }; // Start before first page if PartA1 is page 3

    if (loading) {
        return (
            <div className="fixed inset-0 bg-black bg-opacity-50 flex justify-center items-center z-50">
                <div className="bg-white rounded-lg p-8 max-w-md w-full mx-4">
                    <div className="flex items-center justify-center mb-4">
                        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-orange-500"></div>
                    </div>
                    <p className="text-center text-gray-700 font-semibold">Loading HPC report data...</p>
                </div>
            </div>
        );
    }

    if (error && !reportData) {
        return (
            <div className="fixed inset-0 bg-black bg-opacity-50 flex justify-center items-center z-50">
                <div className="bg-white rounded-lg p-8 max-w-md w-full mx-4">
                    <div className="text-center">
                        <h2 className="text-xl text-red-600 font-semibold mb-2">Error Loading Report</h2>
                        <p className="text-gray-700 mb-4">We encountered an issue fetching the HPC report.</p>
                        <div className="text-sm text-gray-500 bg-red-50 p-3 rounded-md mb-4">{error}</div>
                        <div className="flex space-x-3 justify-center">
                            <button
                                onClick={fetchReportData}
                                className="bg-blue-500 hover:bg-blue-600 text-white font-semibold py-2 px-4 rounded shadow"
                            >
                                Try Again
                            </button>
                            <button
                                onClick={onClose}
                                className="bg-gray-300 hover:bg-gray-400 text-gray-800 font-semibold py-2 px-4 rounded shadow"
                            >
                                Close
                            </button>
                        </div>
                    </div>
                </div>
            </div>
        );
    }

    const dataForPartA1 = reportData?.part_a_1 || null;
    const dataForPartA2 = reportData?.part_a_2 || null;
    const dataForPartB = reportData?.part_b || [];
    const dataForPartC = reportData?.part_c || [];

    return (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex justify-center items-center z-50">
            <div className="bg-white rounded-lg w-full max-w-8xl h-full max-h-[95vh] overflow-hidden flex flex-col mx-4">
                {/* Header */}
                <div className="flex justify-between items-center p-6 border-b bg-gradient-to-r from-orange-50 to-blue-50">
                    <div className="flex items-center space-x-4">
                        <div className="bg-gradient-to-r from-orange-500 to-orange-600 text-white p-3 rounded-full shadow-lg">
                            <AssessmentIconSVG type="books" size={24} selected={true} />
                        </div>
                        <div>
                            <h2 className="text-xl font-bold text-gray-800">HPC Performance Report</h2>
                            <p className="text-gray-600 text-sm">
                                Student: <span className="font-semibold text-blue-600">{studentName || 'Student'}</span>
                            </p>
                        </div>
                    </div>

                    <div className="flex space-x-3">
                        {/*<button*/}
                        {/*    onClick={handleExportPdf}*/}
                        {/*    disabled={!reportData || exportingPdf}*/}
                        {/*    className="no-print bg-gradient-to-r from-orange-500 to-orange-600 hover:from-orange-600 hover:to-orange-700 text-white font-semibold py-2 px-4 rounded-lg shadow-md flex items-center transition duration-150 ease-in-out disabled:opacity-50 disabled:cursor-not-allowed"*/}
                        {/*>*/}
                        {/*    <AssessmentIconSVG type="computer" size={18} selected={true}/>*/}
                        {/*    <span className="ml-2">*/}
                        {/*        {exportingPdf ? 'Generating PDF...' : 'Export Full Report as PDF'}*/}
                        {/*    </span>*/}
                        {/*</button>*/}

                        <button
                            onClick={onClose}
                            className="bg-gray-200 hover:bg-gray-300 text-gray-800 font-semibold py-2 px-4 rounded-lg shadow-md transition duration-150 ease-in-out"
                        >
                            Close
                        </button>
                    </div>
                </div>

                {/* Report content with perfect A4 spacing and no extra margins */}
                <div className="flex-1 overflow-y-auto" style={{ backgroundColor: '#F7FAFF' }}>
                    <div className="py-8">
                        <div id="teacher-hpc-report-content" ref={reportRef} style={{
                            display: 'block',
                            margin: '0 auto',
                            padding: '0',
                            maxWidth: '210mm'
                        }}>
                            {/* Each page is exactly A4 size with proper spacing */}
                            <div style={{ marginBottom: '2rem' }}>
                                <PartA1
                                    data={dataForPartA1}
                                    pageNumber={dataForPartA1 ? (pageCounter.current = 3) : undefined}
                                />
                            </div>

                            <div style={{ marginBottom: '2rem' }}>
                                <PartA2
                                    data={dataForPartA2}
                                    pageNumber={dataForPartA2 ? (pageCounter.current = 4) : undefined}
                                />
                            </div>

                            {/* Part B Domain and Feedback Pages */}
                            {dataForPartB.map((domainEntry, index) => {
                                const assignment = domainEntry.assignments && domainEntry.assignments.length > 0
                                    ? domainEntry.assignments[0]
                                    : null;

                                // Page numbers as per images for first few domains, then sequential
                                let domainPageNum, feedbackPageNum;
                                if (index === 0) { // Domain 1
                                    domainPageNum = pageCounter.current = 5;
                                    feedbackPageNum = pageCounter.current = 6;
                                } else if (index === 1) { // Domain 2
                                    domainPageNum = pageCounter.current = 7;
                                    feedbackPageNum = pageCounter.current = 8;
                                } else { // Other domains
                                    domainPageNum = ++pageCounter.current;
                                    feedbackPageNum = ++pageCounter.current;
                                }

                                return (
                                    <React.Fragment key={domainEntry.domain_id || `domain-${index}`}>
                                        <div style={{ marginBottom: '2rem' }}>
                                            <PartBDomainPage
                                                domain={domainEntry}
                                                assignment={assignment}
                                                pageNumber={domainPageNum}
                                            />
                                        </div>
                                        <div style={{ marginBottom: '2rem' }}>
                                            <TeacherFeedbackPage
                                                assignment={assignment}
                                                proficiencyMarks={assignment?.proficiency_marks}
                                                observationalNotes={assignment?.observational_notes || assignment?.teacher_feedback}
                                                pageNumber={feedbackPageNum}
                                            />
                                        </div>
                                    </React.Fragment>
                                );
                            })}

                            {/* Part C - Page 17 */}
                            <div style={{ marginBottom: '2rem' }}>
                                <PartC
                                    data={dataForPartC}
                                    pageNumber={dataForPartC.length > 0 ? 17 : undefined}
                                />
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default TeacherHPCReportPage;